import PropTypes from 'prop-types';
import React from 'react';

import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { Grid, GridColumn } from '@/design-system/atoms/Grid';
import { RichTextEditor } from '@/design-system/atoms/RichTextEditor';
import { Title } from '@/design-system/atoms/Title';
import { useObserver } from '@/hooks/useObserver';

import { DirectoryMain } from './components/DirectoryMain';
import { DirectorySearchBar } from './components/DirectorySearchBar';
import { DirectoryProvider } from './context/DirectoryContext';
import styles from './Directory.module.scss';

const Directory = ({
    title,
    description,
    inputFieldLabel = 'Search by Department',
    coveo,
    locationId,
    noResultsComponent,
    errorComponent,
    searchButtonAlt = 'Search Locations',
    ...props
}) => {
    const { ref, inView } = useObserver({ triggerOnce: true, threshold: 0.1 });
    const [show, setShow] = React.useState(false);

    React.useEffect(() => {
        if (inView) {
            setShow(true);
        }
    }, [inView]);
    if (!show) {
        return <div ref={ref} />;
    }
    return (
        <DirectoryProvider
            coveo={coveo}
            locationId={locationId}
            noResultsComponent={noResultsComponent}
            errorComponent={errorComponent}
        >
            <div className={styles['directory']} {...props}>
                <ContentContainer>
                    <Grid className={styles[`directory__grid`]}>
                        <GridColumn
                            colSpan={{ sm: 4, md: 6, lg: 8 }}
                            className={styles['directory__header']}
                        >
                            <Title variant={Title.VARIANT.H3} tag={Title.TAG.H2} content={title} />
                            {description && (
                                <div className={styles['directory__description']}>
                                    <RichTextEditor text={description} />
                                </div>
                            )}
                        </GridColumn>
                        <GridColumn
                            colSpan={{ sm: 4, md: 6, lg: 6 }}
                            className={styles['directory__searchbar']}
                        >
                            <DirectorySearchBar
                                inputFieldLabel={inputFieldLabel}
                                searchButtonAlt={searchButtonAlt}
                                analytics={{
                                    eventName: 'search',
                                    componentName: 'Directory',
                                    componentTitle: title,
                                }}
                            />
                        </GridColumn>
                        <GridColumn
                            colSpan={{ sm: 4, md: 6, lg: 12 }}
                            className={styles['directory__main']}
                        >
                            <DirectoryMain
                                analytics={{
                                    eventName: 'search',
                                    componentName: 'Directory',
                                    componentTitle: title,
                                }}
                            />
                        </GridColumn>
                    </Grid>
                </ContentContainer>
            </div>
        </DirectoryProvider>
    );
};

Directory.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    inputFieldLabel: PropTypes.string,
    locationId: PropTypes.string,
    coveo: PropTypes.object,
    noResultsComponent: PropTypes.node,
    errorComponent: PropTypes.node,
};

export default Directory;
